"use client";
import { useCurrency } from "@/contexts/CurrencyContext";
import { busSearchReducer, initialState } from "@/reducers/busSearchReducer";
import { createBusSearchSchema } from "@/schemas/busSearchSchema";
import { busServices } from "@/services/busServices";
import { staticDataService } from "@/services/staticService";
import { getPassengerTypeBreakdownVisibility } from "@/utils/bus/appConfigUtils";
import { formatDate } from "@/utils/dateFormatter";
import { getLocalizedPath } from "@/utils/pathUtils";
import { useTranslationDetector } from "@/hooks/useTranslationDetector";
import { useLocale } from "next-intl";
import { useCallback, useEffect, useReducer } from "react";

export const useBusSearch = (initialData = {}) => {

  const showPassengerTypeBreakdown = getPassengerTypeBreakdownVisibility();

  // Initial data ile state'i merge et
  // Eğer useTomorrowAsDefault true ise ve departureDate yoksa, departureDate'i yarın yap
  const today = new Date();
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  
  // Eğer initialData'da departureDate yoksa, useTomorrowAsDefault flag'ine göre bugün veya yarın seç
  const defaultDate = initialData.departureDate 
    ? initialData.departureDate 
    : (initialData.useTomorrowAsDefault ? tomorrow : today);
  
  const mergedInitialState = {
    ...initialState,
    ...initialData,
    departureDate: defaultDate,
  };

  const [state, dispatch] = useReducer(busSearchReducer, mergedInitialState);
  const locale = useLocale();
  const { currency } = useCurrency();
  const t = useTranslationDetector("bus");


  const busSearchSchema = createBusSearchSchema(t);

  const setLoading = (loading) => {
    dispatch({ type: "SET_LOADING", payload: loading });
  };

  const setDepartureDate = (departureDate) => {
    dispatch({ type: "SET_DEPARTURE_DATE", payload: departureDate });
  };

  const setDepartureCity = useCallback((departureCity) => {
    dispatch({ type: "SET_DEPARTURE_CITY", payload: departureCity });
  }, []);

  const setReturnCity = useCallback((returnCity) => {
    dispatch({ type: "SET_RETURN_CITY", payload: returnCity });
  }, []);

  const toggleDepartureAndReturn = () => {
    dispatch({ type: "TOGGLE_DEPARTURE_AND_RETURN" });
  };

  const setPassengersAndCabinClass = (data) => {
    dispatch({ type: "SET_PASSENGERS_AND_CABIN_CLASS", payload: data });
  };

  const setLatestBusSearches = (latestBusSearches) => {
    dispatch({
      type: "SET_LATEST_BUS_SEARCHES",
      payload: latestBusSearches,
    });
  };

  const setPopularCities = (popularCities) => {
    dispatch({ type: "SET_POPULAR_CITIES", payload: popularCities });
  };

  const setPublicHolidays = (publicHolidays) => {
    dispatch({ type: "SET_PUBLIC_HOLIDAYS", payload: publicHolidays });
  };

  // Bus için sadece gerekli fonksiyonları kullan
  const getPopularCities = async () => {
    try {
      const popularCitiesResponse = await busServices.city.getSearchPopulars({
        LanguageCode: locale,
      });

      const list = Array.isArray(popularCitiesResponse?.PopularDestinationsList)
        ? popularCitiesResponse.PopularDestinationsList
        : [];

      const limited = list.length > 10 ? list.slice(0, 10) : list;

      const mapped = limited.map((item) => {
        const cityName =
          item?.value || item?.label || item?.lebel || item?.Title || "";
        const cityId =
          item?.id ||
          (cityName ? cityName.toLowerCase().replace(/\s+/g, "-") : "");
        return {
          CityName: cityName,
          AutoCompleteResult: {
            code: "", // bus için kod bilgisi yok
            id: String(cityId || ""),
            label: cityName,
            value: cityName,
            country: "TR",
            cityName: cityName,
            isCity: true,
          },
        };
      });

      setPopularCities(mapped.length ? mapped : null);
    } catch (error) {
      console.error(
        "getPopularCities: Popular cities yüklenirken hata oluştu:",
        error,
      );
    }
  };

  // Lazy-load flags and loaders
  const ensurePopularLoaded = useCallback(async () => {
    try {
      // Load if missing only
      if (!state.popularCities) {
        await getPopularCities();
      }
    } catch (_) {
      // no-op, errors already logged inside fetchers
    }
  }, [state.popularCities]);

  // Search for bus terminals using bus autocomplete service
  const searchForBusTerminals = useCallback(
    async (query) => {
      const response = await busServices.search.autocomplete(query, locale);
      return response;
    },
    [locale],
  );

  // Şehir kodundan şehir bilgisi getiren fonksiyon
  const getCityInfoByCode = useCallback(
    async (cityCode) => {
      try {
        const response = await busServices.search.autocomplete(
          cityCode,
          locale,
        );

        // Response'dan ilk uygun sonucu bul
        if (response && response.length > 0) {
          // Tam eşleşme yoksa ilk sonucu döndür
          return response?.[0] || {};
        }

        return null;
      } catch (error) {
        console.error(
          `getCityInfoByCode: ${cityCode} için şehir bilgisi getirilemedi:`,
          error,
        );
        return null;
      }
    },
    [locale],
  );

  // City selection handlers - bus için basitleştirilmiş
  const onSelectFromCity = useCallback(
    (city) => {
      setDepartureCity(city);
    },
    [setDepartureCity],
  );

  const onSelectToCity = useCallback(
    (city) => {
      setReturnCity(city);
    },
    [setReturnCity],
  );

  // Utility functions
  const formatDateForUrl = useCallback((date) => {
    return formatDate(date, { format: "YYYY-MM-DD" });
  }, []);

  // Bus için URL oluşturma
  // TODO burak: servisler gelince otobuse gore formatı duzenlenmeli aynı şekilde generateSearchUrl burdaki bus ile alakalı url oluşturucuda düzenlenmeli hatta bu iki fonk ortak hale gelmeli.
  const parseCampaignIdsFromUrl = (searchString) => {
    try {
      let search = searchString;
      if (!search) {
        if (typeof window === 'undefined') return [];
        search = window.location.search || '';
      }
      const params = new URLSearchParams(search);
      const raw = params.get('campaignId');
      if (!raw) return [];
      return String(raw)
        .split(',')
        .map((v) => encodeURIComponent(v.trim()))
        .filter((v) => v.length > 0);
    } catch {
      return [];
    }
  };

  const generateBusSearchUrl = useCallback(() => {
    // Flight search ile aynı formatta tek parametre olarak birleştir
    const stationInfo = `${state.departureCity?.id || "from"}-${state.returnCity?.id || "to"}`;
    const depDate = formatDateForUrl(state.departureDate);

    // Query parametreleri oluştur
    const queryParams = new URLSearchParams();

    // Bus yolcu bilgilerini ekle (sadece 0'dan büyük olanları)
    // Bus için: adult, young, child
    if (showPassengerTypeBreakdown) {
      if (state.passengers.adult > 0)
        queryParams.set("adult", state.passengers.adult.toString());
      if (state.passengers.young > 0)
        queryParams.set("young", state.passengers.young.toString());
      if (state.passengers.child > 0)
        queryParams.set("child", state.passengers.child.toString());
    }

    const campaignIds = parseCampaignIdsFromUrl();
    if (campaignIds && campaignIds.length > 0) {
      queryParams.set("campaignId", campaignIds.join(','));
    }

    const queryString = queryParams.toString();

    const fullPath = getLocalizedPath('/bus-ticket/one-way/', locale);

    const languageCode = locale == "tr" ? "" : `/${locale}`;

    const baseUrl = `${languageCode}${fullPath}${stationInfo}/${depDate}`;

    return queryString ? `${baseUrl}?${queryString}` : baseUrl;
  }, [
    state.departureCity,
    state.returnCity,
    state.departureDate,
    formatDateForUrl,
    state.passengers,
  ]);

  // Validation
  const validateForm = useCallback(() => {
    const formData = {
      departureCity: state.departureCity,
      returnCity: state.returnCity,
    };

    try {
      busSearchSchema.parse(formData);
      return {};
    } catch (error) {
      const errors = {};
      error.errors.forEach((err) => {
        errors[err.path[0]] = err.message;
      });
      return errors;
    }
  }, [state.departureCity, state.returnCity]);

  const getPublicHolidays = async () => {
    try {
      const publicHolidaysResponse = await staticDataService.getStaticData({
        LanguageCode: locale.toUpperCase(),
        PageName: "PublicHolidays",
        GroupNames: [
          new Date().getMonth() === 11
            ? new Date().getFullYear() + 1
            : new Date().getFullYear(),
        ],
      });
      const publicHolidays = JSON.parse(
        publicHolidaysResponse?.ContentParts[0]?.ContentGroupKeys[0]
          ?.ContentGroupValues[0]?.Value,
      ).map((item) => ({
        date: item.date,
        title: item.localName,
      }));
      setPublicHolidays(publicHolidays);
    } catch (error) {
      console.error(
        "getPublicHolidays: Public holidays yüklenirken hata oluştu:",
        error,
      );
    }
  };

  const getBusDefaultOrigin = async () => {
    try {
      const defaultCityOrigin = t("defaultCityOrigin");
      if (defaultCityOrigin !== "" && defaultCityOrigin !== null && defaultCityOrigin !== undefined) {
        const response = await busServices.search.autocomplete(
          defaultCityOrigin,
            locale,
          );
        setDepartureCity(response?.[0] || null);
      }else{
        setDepartureCity(null);
      }
    } catch (error) {
      console.error(
        "getBusDefaultOrigin: Bus default origin yüklenirken hata oluştu:",
        error,
      );
    }
  };

  useEffect(() => {
    const STORAGE_KEY = initialData.storageKey || "latestBusSearches";
    const latestBusSearches = localStorage.getItem(STORAGE_KEY);
    if (latestBusSearches) {
      const parsedSearches = JSON.parse(latestBusSearches);
      // Eğer 3'ten fazla arama varsa, son 3 tanesini al
      const limitedSearches =
        parsedSearches.length > 3 ? parsedSearches.slice(0, 3) : parsedSearches;
      setLatestBusSearches(limitedSearches);
    }

    // Eğer initial state'de departureCity yoksa default origin'i getir
    if (!initialData.initialDepartureCityCode && locale) {
      getBusDefaultOrigin();
    }
  }, [locale]);

  // Initial data'da şehir kodları varsa API'den gerçek şehir bilgilerini getir
  useEffect(() => {
    const loadInitialCityData = async () => {
      try {
        // Departure city kodunu kontrol et
        if (initialData.initialDepartureCityCode && !state.departureCity) {
          const departureCityData = await getCityInfoByCode(
            initialData.initialDepartureCityCode,
          );
          if (departureCityData) {
            setDepartureCity(departureCityData);
          }
        }

        // Return city kodunu kontrol et
        if (initialData.initialReturnCityCode && !state.returnCity) {
          const returnCityData = await getCityInfoByCode(
            initialData.initialReturnCityCode,
          );
          if (returnCityData) {
            setReturnCity(returnCityData);
          }
        }
      } catch (error) {
        console.error(
          "loadInitialCityData: Initial şehir bilgileri yüklenirken hata oluştu:",
          error,
        );
      }
    };

    // Sadece locale yüklendiyse çalıştır
    if (
      locale &&
      (initialData.initialDepartureCityCode ||
        initialData.initialReturnCityCode)
    ) {
      loadInitialCityData();
    }
  }, [
    locale,
    initialData.initialDepartureCityCode,
    initialData.initialReturnCityCode,
  ]);

  return {
    // Bus-specific state mapping
    fromCity: state.departureCity,
    toCity: state.returnCity,
    departureDate: state.departureDate,
    passengers: state.passengers,
    latestBusSearches: state.latestBusSearches,
    popularDestinations: state.popularCities,
    publicHolidays: state.publicHolidays,
    useTomorrowAsDefault: state.useTomorrowAsDefault,

    // Actions
    setDepartureDate,
    setDepartureCity,
    setReturnCity,
    toggleFromAndTo: toggleDepartureAndReturn,
    setPassengersAndCabinClass,

    // Service functions
    searchForBusTerminals,
    onSelectFromCity,
    onSelectToCity,
    ensurePopularLoaded,

    // Utility functions
    validateForm,
    generateBusSearchUrl,
    refreshPublicHolidays: getPublicHolidays,

    // State for compatibility
    state,
  };
};
